import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, CalendarClock, CheckCircle2, ChevronLeft, Clock, Download, History, LayoutList, Search, Send, UserSearch, X, type LucideIcon,
} from 'lucide-react';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { Skeleton } from '../components/Skeleton';
import { HiringNoticeTable, noticeRowKey } from '../components/hiring/HiringNoticeTable';
import { HiringPager } from '../components/hiring/HiringPager';
import { HiringPanel, type HiringPanelSubject, type ReplacingOpen } from '../components/hiring/HiringPanel';
import { HiringRequestPill, HiringRequestWaitingTag } from '../components/hiring/HiringRequestCard';
import { HiringRequestWindow } from '../components/hiring/HiringRequestWindow';
import { LinkHiringModal, type LinkHiringInitial } from '../components/hiring/LinkHiringModal';
import { ManageBudgetsLink } from '../components/hiring/ManageBudgetsLink';
import { HIRING_BUTTONS, HOUSE_TABS, houseTabClass, withCount } from '../components/hiring/hiringLooks';
import { RaiseHiringModal, type RaiseHiringSubject, type RaiseLinkInsteadPick } from '../components/hiring/RaiseHiringModal';
import {
  DELETED_WORDS,
  hiringErrorText,
  hiringStatusOf,
  isHiringOff,
  isRaiseOff,
  requestAskedByShort,
  requestLine,
  requestReasonLine,
  requestSubjectName,
  deletedInRecruitment,
} from '../components/hiring/hiringText';
import {
  DASHBOARD_REQUEST_WORDS,
  DASHBOARD_WORDS,
  DASH_STATUSES,
  DASH_STATUS_DOT,
  DASH_STATUS_WORDS,
  DOWNLOAD_WORDS,
  attentionHover,
  candidatesTotal,
  dashPlaceOf,
  dashStatusOfLink,
  dashWordOfLink,
  designationOf,
  numberOrDash,
  storeWords,
  updatedWords,
} from '../components/hiring/hiringText.dashboard';
import { NOTICE_WORDS } from '../components/hiring/hiringText.notice';
import { hiringExportErrorText, hiringExportFileName, hiringExportSavedLine, hiringExportSheets } from '../components/hiring/hiringExport';
import { forgetHiringAccess, hiringUserKeyOf, useHiringAccess } from '../components/hiring/useHiringAccess';
import { forgetHiringRequests, useHiringRequests } from '../components/hiring/useHiringRequests';
import {
  NO_CANCEL, NO_RAISE, cancelAccessOf, raiseAccessOf, storeHiringKey, type CancelAccess, type RaiseAccess,
} from '../components/hiring/useStoreHiring';
import {
  hiringLinksAPI,
  type HiringDashStatus,
  type HiringDashboardList,
  type HiringLink,
  type HiringNoticeList,
  type HiringNoticeRow,
  type HiringRequest,
  type HiringStoreOption,
} from '../services/api';
import { hiringOpeningAPI } from '../services/hiringOpeningApi';
import { useAuthStore } from '../stores/authStore';
import { downloadTableXlsx } from '../utils/tableXlsx';
import { useDashboardList } from '../components/dashboard/useDashboardList';
import { HIRING_LIST } from '../components/dashboard/lists/hiring';

/**
 * Dashboard › Hiring (approved mock-up of 15 Oct 2026, panel 1).
 *
 * Every opening in Recruitment this project follows - for a store, or with no store - one
 * row each, newest change first, 12 to a page: where it is (the store, or "No store" in
 * grey), what it is (the budget's designation and the opening's number), the address it
 * hires for - its Address, City and State as Recruitment has the opening now, else as it
 * was linked (26 Oct 2026, the product owner) - its status as a dot and one word (Being
 * raised, Hiring, Filled, Needs attention, with the reason on hover), how many positions
 * are open, how many candidates the last read counted and when it last changed. One search
 * box (a store's name or code, the opening's number, the budget's name, the address, the
 * city or the State) and one status filter with its counts narrow it. A row opens the
 * opening view (HiringPanel).
 *
 * NO REFRESH BUTTON. The tab asks Recruitment afresh (`refresh`) every time it is opened and
 * after every action taken from it; a search, a status or another page is served from what
 * that read just kept. The server reuses a read of the same project and budget younger than
 * 15 seconds even when asked afresh (hiring-link.service REFRESH_FLOOR_MS), so landing, flipping
 * tabs or several people at once cost one read; this tab also asks afresh at most once in 15 s.
 *
 * WHO SEES WHAT. Raise hiring (the filled button) only for a holder of "Raise & cancel
 * hiring" where the list says Raise is on; Ask for hiring, in its place, for a person who
 * may ask and may not raise; Link an opening for a person who may manage; Open Manage
 * Budgets as ManageBudgetsLink decides. The strip "N requests waiting · Review" shows only
 * while hiring requests wait; Review lists them here, and a row opens its window.
 *
 * NOTICE PERIOD (16 Oct 2026, the product owner): a sixth tab in the same strip, "Notice period
 * (N)", lists the people deployed at the stores this person looks after who are serving notice
 * (HiringNoticeTable), so hiring to replace them is raised from here - not noted down from the
 * Deployment dashboard and raised from the Stores page. It is read with the list (on landing,
 * after every action, for each search), so its count is there like the others'; the search box
 * narrows it by store or person, and the count follows the search. A row's Raise hiring opens
 * the Raise window for that store, "Replacing someone", that person chosen (Ask for hiring in
 * its place for a person who may ask and may not raise); Link an opening opens the Link window
 * for that store; a person whom hiring already replaces offers their request, or the store's
 * hiring, instead.
 *
 * NOT RAISED (22 Oct 2026, the product owner): the home screen's "Notice period, no hiring"
 * card counts the people on that tab whom no hiring replaces yet, and opens here on exactly
 * them: ?list=notice-not-raised opens the Notice period tab narrowed to them (the server's
 * own narrowing, so the pages and the count in brackets are of those rows), with a chip that
 * says so. Removing the chip, or picking any tab, takes ?list out of the address.
 *
 * DOWNLOAD (28 Oct 2026, the product owner): the first button of the header row saves ONE Excel
 * file of every opening on the open tab and search - all pages, not only the twelve shown - with
 * each one's details and team, and every candidate Recruitment lists for it, by name and full
 * mobile number, with their stage (components/hiring/hiringExport.ts). For everyone who may see
 * hiring, as the opening panel is. The server reads it all in one call (POST …/export, the tab
 * and the search in its body: hiringOpeningAPI.exportHiring), the browser writes the file, and
 * nothing records it. Hidden on the Notice period tab (people, not openings) and on the requests
 * views; disabled while the tab has nothing, and while it is being prepared ("Preparing…"). A
 * refusal is one line under the toolbar and the table stays; openings Recruitment could not be
 * read for are marked in the file, never as 0, and counted in an amber line once it is saved.
 *
 * What is typed in the search box stays in this page and in the request body: never in the
 * address, a file name, a log line or an analytics event. The table counts candidates and names
 * none; only the Download's file names them.
 */

type View = 'openings' | 'requests_waiting' | 'requests_past';

const NO_REQUESTS: readonly HiringRequest[] = [];
const SEARCH_WAIT_MS = 300;
/** The longest text the server's search takes. */
const SEARCH_MAX = 60;
/**
 * Two fresh reads closer together than this ask Recruitment once: the second is served from
 * what the first kept (the proposal's 15-second guard, kept by the server too).
 * A window that answers several times in a row (a raise being checked) costs one read.
 */
const FRESH_FLOOR_MS = 15_000;

const PRIMARY = HIRING_BUTTONS.primary;
/**
 * Nine columns since 26 Oct 2026 (Address, City and State added), cells px-3 apart (px-6 at the
 * table's edges). The table is as wide as its box and its columns are set, not sized by what is
 * in them (`table-fixed`): Status, Open, Candidates and Updated as wide as their words; Store,
 * Opening, City and State a share of the table; Address the rest, so the most on a wide screen.
 * A text cell fills its column and, when longer, ends in "…" and shows whole on hover - so the
 * table fits its box at 1360px and no column is cut while it has room. Status is as wide as
 * "Needs attention": the rare "Deleted in Recruitment" takes two lines. Below 75rem the table
 * scrolls sideways.
 */
const TABLE = 'w-full min-w-[75rem] table-fixed text-sm';
/** Store, Opening, Address (the rest), City, State, Status, Open, Candidates, Updated. */
const COLUMN_WIDTHS = ['w-[17.5%]', 'w-[15%]', '', 'w-[9.5%]', 'w-[11%]', 'w-[9.5rem]', 'w-14', 'w-24', 'w-[7.5rem]'] as const;
const TH = 'whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold text-gray-500 first:pl-6 last:pr-6';
const CELL = 'px-3 py-3 first:pl-6 last:pr-6';
const TD = `whitespace-nowrap ${CELL}`;

/** The icon before each status in the filter (the house strip: an icon, then the name and its count). */
const STATUS_ICON: Record<HiringDashStatus | '', LucideIcon> = {
  '': LayoutList,
  being_raised: Send,
  hiring: UserSearch,
  filled: CheckCircle2,
  needs_attention: AlertTriangle,
};

const panelSubjectOf = (link: HiringLink): HiringPanelSubject =>
  (link.subject.kind === 'store'
    ? { kind: 'store', storeName: link.subject.storeName || link.subject.storeCode || 'this store', storeCode: link.subject.storeCode || '', storeId: link.subject.storeId ?? undefined }
    : { kind: 'role', roleLabel: designationOf(link) });

/** A row of the Notice period tab's store, as the Raise window is given a store's row. */
const raiseSubjectOfNotice = (row: HiringNoticeRow): RaiseHiringSubject => ({
  kind: 'store',
  storeId: row.store.storeId,
  storeName: row.store.storeName || row.store.storeCode || 'this store',
  storeCode: row.store.storeCode ?? '',
  address: row.store.address ?? '',
  city: row.store.city ?? '',
  state: row.store.state ?? '',
  pincode: row.store.pincode ?? '',
  hasMapLocation: row.store.hasMapLocation === true,
  status: row.store.status ?? '',
  staffingStatus: row.store.staffingStatus ?? '',
});

/** A store of the Raise window's own store search, as the Raise window is given a store's row. */
const raiseSubjectOfOption = (store: HiringStoreOption): RaiseHiringSubject => ({
  kind: 'store',
  storeId: store.storeId,
  storeName: store.storeName || store.storeCode || 'this store',
  storeCode: store.storeCode ?? '',
  address: store.address ?? '',
  city: store.city ?? '',
  state: store.state ?? '',
  pincode: store.pincode ?? '',
  hasMapLocation: store.hasMapPoint === true,
  status: store.status ?? '',
  staffingStatus: store.staffingStatus ?? '',
});

/** A row's store whose links could not be read: none, as before they are known. */
const NO_ROW_LINKS: readonly HiringLink[] = [];

/** A code as the server compares it: trimmed and upper-cased. */
const codeOf = (code: string | null | undefined): string => String(code ?? '').trim().toUpperCase();

/** "Link it instead", from the Raise window: the budget, the opening and the store, when it names one. */
const linkPickOf = (pick: RaiseLinkInsteadPick): LinkHiringInitial => ({
  budgetId: pick.budgetId,
  openingId: pick.openingId,
  store: pick.store ? { storeId: pick.store.storeId, storeName: pick.store.storeName, storeCode: pick.store.storeCode } : null,
});

/** An Address, City or State cell: the text on one line, ending in "…" past `max` with the whole of it on hover; "–" when not known. */
function PlaceText({ text }: { text: string | null }) {
  if (!text) return <span className="text-gray-400">–</span>;
  return <span title={text} className="block truncate">{text}</span>;
}

function StatusWord({ link }: { link: HiringLink }) {
  const status = dashStatusOfLink(link);
  if (!status) return <span className="text-gray-400">–</span>;
  return (
    <span className="inline-flex items-center gap-2" title={attentionHover(link) ?? undefined}>
      <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-full ${DASH_STATUS_DOT[status]}`} />
      {dashWordOfLink(link)}
    </span>
  );
}

export default function HiringDashboardTab({ actionSlot }: { actionSlot?: HTMLElement | null } = {}) {
  const projectId = useAuthStore((state) => state.selectedProjectId);
  const userKey = useAuthStore((state) => hiringUserKeyOf(state.user));
  const { canManage, canRaise, canSeeRequests } = useHiringAccess(projectId, userKey);
  const hiringRequests = useHiringRequests(projectId, userKey, canSeeRequests);
  const requestsShown = hiringRequests.status === 'ready';
  // A person who may send a hiring request and does not hold "Raise & cancel hiring": they ask.
  const canAsk = requestsShown && hiringRequests.can.send && !canRaise;

  // ── the list: one page, its counts, and what it says of Raise and Cancel ──
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<HiringDashStatus | ''>('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<HiringDashboardList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [raise, setRaise] = useState<RaiseAccess>(NO_RAISE);
  const [cancel, setCancel] = useState<CancelAccess>(NO_CANCEL);
  // Bumped after every action: the list is read again, and Recruitment asked afresh.
  const [reads, setReads] = useState(0);
  // The next read asks Recruitment afresh: on opening the tab, and after an action.
  const fresh = useRef(true);
  // When Recruitment was last asked afresh, for the floor above.
  const lastFresh = useRef(0);
  const listReq = useRef(0);
  const projectNow = useRef(projectId);
  projectNow.current = projectId;

  // ── what is open over the list ──
  const [view, setView] = useState<View>('openings');
  const [panelFor, setPanelFor] = useState<HiringLink | null>(null);
  // The link the open panel was last answered with, for when the rows no longer list it.
  const [panelAnswer, setPanelAnswer] = useState<HiringLink | null>(null);
  const panelOpenOn = useRef<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [asking, setAsking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkingPick, setLinkingPick] = useState<LinkHiringInitial | null>(null);
  const [requestOpen, setRequestOpen] = useState<HiringRequest | null>(null);
  const [past, setPast] = useState<HiringRequest[]>([]);
  const [pastCursor, setPastCursor] = useState<string | null>(null);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastReads, setPastReads] = useState(0);
  const pastReq = useRef(0);

  // ── the Notice period tab: its page, its last answer, and what is open from one of its rows ──
  const [showNotice, setShowNotice] = useState(false);
  // The address can open the tab narrowed to the people whose hiring is not raised yet (the
  // home screen's card). Read from the address on every render, never copied into state.
  const { list: listInAddress, closeList } = useDashboardList();
  const notRaisedOnly = listInAddress === HIRING_LIST.noticeNotRaised;
  const noticeOpen = showNotice || notRaisedOnly;
  const [noticePage, setNoticePage] = useState(1);
  const [notice, setNotice] = useState<HiringNoticeList | null>(null);
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [noticeError, setNoticeError] = useState<string | null>(null);
  // Bumped after every action, and by "Try again": the tab is read again.
  const [noticeReads, setNoticeReads] = useState(0);
  const noticeReq = useRef(0);
  // Raise hiring (or Ask for hiring) from a row: the row, which of the two the row offered, and the store's links as just read.
  const [rowRaise, setRowRaise] = useState<{ row: HiringNoticeRow; mode: 'raise' | 'ask'; links: readonly HiringLink[] } | null>(null);
  // Link an opening from a row (or "Link it instead" from its Raise window): the row, and the pick to open on.
  const [rowLink, setRowLink] = useState<{ row: HiringNoticeRow; pick: LinkHiringInitial | null } | null>(null);
  // The store's hiring window, opened from a row whose person hiring already replaces: its links as just read.
  const [storePanel, setStorePanel] = useState<{ row: HiringNoticeRow; links: HiringLink[] } | null>(null);
  // "Raise again" (or "Ask for hiring") on an opening deleted in Recruitment: the Raise window for
  // its store (`entry="store"`; none for a hiring with no store), that store's followed openings,
  // and the deleted link - which a raise sent from that window stops following.
  const [deletedRaise, setDeletedRaise] = useState<{
    mode: 'raise' | 'ask'; subject: RaiseHiringSubject | null; links: readonly HiringLink[]; deleted: HiringLink;
  } | null>(null);
  const deletedReq = useRef(0);
  const [storeOpening, setStoreOpening] = useState<string | null>(null);
  const storeReq = useRef(0);

  // ── the Download: one file of every opening of the tab and search ──
  const [downloading, setDownloading] = useState(false);
  // What the last download has to say under the toolbar - a refusal, or what the saved file could
  // not read - with the tab and search it was of: shown only while that tab and search are on screen.
  const [downloadNote, setDownloadNote] = useState<{ on: string; kind: 'error' | 'saved'; text: string } | null>(null);
  // A download asked for in one project is dropped when the page moves to another.
  const downloadReq = useRef(0);
  // A window opened for one project does not follow the page to another, not even for one render.
  const [windowsFor, setWindowsFor] = useState(projectId);
  useEffect(() => {
    setTyped('');
    setSearch('');
    setStatus('');
    setPage(1);
    setList(null);
    setRaise(NO_RAISE);
    setCancel(NO_CANCEL);
    fresh.current = true;
    lastFresh.current = 0;
    setView('openings');
    setPanelFor(null);
    setPanelAnswer(null);
    setRaising(false);
    setAsking(false);
    setLinking(false);
    setLinkingPick(null);
    setRequestOpen(null);
    setShowNotice(false);
    setNoticePage(1);
    setNotice(null);
    setNoticeError(null);
    setRowRaise(null);
    setRowLink(null);
    setStorePanel(null);
    setStoreOpening(null);
    storeReq.current += 1;
    setDeletedRaise(null);
    deletedReq.current += 1;
    setDownloading(false);
    setDownloadNote(null);
    downloadReq.current += 1;
    setWindowsFor(projectId);
  }, [projectId]);
  const windowsOpen = windowsFor === projectId;
  panelOpenOn.current = windowsOpen && panelFor ? panelFor.id : null;
  // Hiring requests went (Raise switched off, or the right taken away) while they were on show.
  const showingRequests = view !== 'openings';
  useEffect(() => {
    if (showingRequests && !requestsShown) setView('openings');
  }, [showingRequests, requestsShown]);

  // The search is sent once typing pauses; a new search starts on the first page.
  useEffect(() => {
    const next = typed.trim().replace(/\s+/g, ' ').slice(0, SEARCH_MAX);
    if (next === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(next);
      setPage(1);
      setNoticePage(1);
    }, SEARCH_WAIT_MS);
    return () => clearTimeout(timer);
  }, [typed, search]);

  /** Read the list again, asking Recruitment afresh: after every action. The Notice period tab is read again with it. */
  const rereadFresh = useCallback(() => {
    fresh.current = true;
    setReads((n) => n + 1);
    setNoticeReads((n) => n + 1);
  }, []);

  const reloadHiringRequests = hiringRequests.reload;
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return undefined;
    }
    // Moved to another project: its search and status are put back first, then it is read.
    if (windowsFor !== projectId) return undefined;
    const req = ++listReq.current;
    const refresh = fresh.current && Date.now() - lastFresh.current >= FRESH_FLOOR_MS;
    fresh.current = false;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(() => hiringLinksAPI.dashboard(projectId, {
        page,
        ...(search ? { q: search } : {}),
        ...(status ? { status } : {}),
        ...(refresh ? { refresh: true } : {}),
      }))
      .then((res) => {
        if (req !== listReq.current || projectNow.current !== projectId) return;
        const data = res?.data?.data;
        if (!data || !Array.isArray(data.links)) throw new Error('unreadable hiring list');
        // Only a read that answered counts against the floor: one that failed is asked afresh again.
        if (refresh) lastFresh.current = Date.now();
        setList(data);
        setRaise(raiseAccessOf(data));
        setCancel(cancelAccessOf(data));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (req !== listReq.current || projectNow.current !== projectId) return;
        setLoading(false);
        // Switched off since the tab opened: the shell takes the tab away.
        if (isHiringOff(err)) forgetHiringAccess(projectId);
        else setError(hiringErrorText(err, DASHBOARD_WORDS.loadFailed));
      });
    return () => {
      listReq.current += 1;
    };
  }, [projectId, windowsFor, page, search, status, reads]);

  // The Notice period tab: read with the list - on landing, for each search, after every action -
  // so its count is in the strip whichever tab is open, and a page of it when asked for.
  useEffect(() => {
    if (!projectId) {
      setNoticeLoading(false);
      return undefined;
    }
    if (windowsFor !== projectId) return undefined;
    const req = ++noticeReq.current;
    setNoticeLoading(true);
    setNoticeError(null);
    Promise.resolve()
      .then(() => hiringLinksAPI.notice(projectId, {
        page: noticePage,
        ...(search ? { q: search } : {}),
        ...(notRaisedOnly ? { notRaisedOnly: true } : {}),
      }))
      .then((res) => {
        if (req !== noticeReq.current || projectNow.current !== projectId) return;
        const data = res?.data?.data;
        if (!data || !Array.isArray(data.rows) || !data.page || !data.oneOffice) throw new Error('unreadable notice list');
        setNotice(data);
        setNoticeLoading(false);
      })
      .catch((err: unknown) => {
        if (req !== noticeReq.current || projectNow.current !== projectId) return;
        setNoticeLoading(false);
        if (isHiringOff(err)) forgetHiringAccess(projectId);
        else setNoticeError(hiringErrorText(err, NOTICE_WORDS.loadFailed));
      });
    return () => {
      noticeReq.current += 1;
    };
  }, [projectId, windowsFor, noticePage, search, noticeReads, notRaisedOnly]);

  // Narrowed or not, the tab starts on its first page.
  const narrowedBefore = useRef(notRaisedOnly);
  useEffect(() => {
    if (narrowedBefore.current === notRaisedOnly) return;
    narrowedBefore.current = notRaisedOnly;
    setNoticePage(1);
  }, [notRaisedOnly]);

  // A page past the end of the tab (an action took rows away): the last page there is.
  const noticeTotal = notice?.page?.total ?? 0;
  const noticeLastPage = Math.max(1, Math.ceil(noticeTotal / (notice?.page?.perPage || 12)));
  useEffect(() => {
    if (notice && noticePage > noticeLastPage) setNoticePage(noticeLastPage);
  }, [notice, noticePage, noticeLastPage]);

  // The strip's count comes with each read of the list; the requests themselves are their
  // own list. When the two disagree, that list is read again (ChannelPlex's own; nothing is
  // asked of Recruitment).
  const listWaiting = list?.requestsWaiting;
  useEffect(() => {
    if (requestsShown && typeof listWaiting === 'number' && listWaiting !== hiringRequests.waiting) reloadHiringRequests();
    // Only when a read of the list says a new number: not every time the requests change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listWaiting]);

  // A page past the end (an action took rows away): the last page there is.
  const perPage = list?.page?.perPage || 12;
  const total = list?.page?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  useEffect(() => {
    if (list && page > lastPage) setPage(lastPage);
  }, [list, page, lastPage]);

  // Past hiring requests, newest first, a page at a time.
  const showingPast = view === 'requests_past' && requestsShown;
  const loadPast = useCallback((cursor?: string) => {
    if (!projectId || projectNow.current !== projectId) return;
    const req = ++pastReq.current;
    setPastLoading(true);
    setPastError(null);
    Promise.resolve()
      .then(() => hiringLinksAPI.requests(projectId, { status: 'past', ...(cursor ? { cursor } : {}) }))
      .then((res) => {
        if (req !== pastReq.current) return;
        const data = res?.data?.data;
        const rows = Array.isArray(data?.requests) ? data.requests : [];
        setPast((prev) => (cursor ? [...prev, ...rows] : rows));
        setPastCursor(data?.nextCursor ?? null);
      })
      .catch((err: unknown) => {
        if (req !== pastReq.current) return;
        if (isHiringOff(err)) forgetHiringAccess(projectId);
        else if (isRaiseOff(err) || hiringStatusOf(err) === 403) forgetHiringRequests(projectId);
        else setPastError(hiringErrorText(err, DASHBOARD_WORDS.loadFailed));
      })
      .finally(() => {
        if (req === pastReq.current) setPastLoading(false);
      });
  }, [projectId]);
  useEffect(() => {
    setPast([]);
    setPastCursor(null);
    setPastError(null);
    if (!showingPast) {
      pastReq.current += 1;
      setPastLoading(false);
      return undefined;
    }
    loadPast();
    return () => {
      pastReq.current += 1;
    };
  }, [showingPast, loadPast, pastReads]);

  // One hiring request as the server has just answered it: into the shared list of those
  // waiting, and into the past ones on show. The list is read again for the strip's count.
  const applyHiringRequest = hiringRequests.apply;
  const applyRequest = useCallback((request: HiringRequest) => {
    if (projectNow.current !== projectId) return;
    applyHiringRequest(request);
    setPast((prev) => {
      if (request.standing === 'waiting') return prev.filter((r) => r.id !== request.id);
      return prev.some((r) => r.id === request.id) ? prev.map((r) => (r.id === request.id ? request : r)) : [request, ...prev];
    });
  }, [projectId, applyHiringRequest]);

  /** Something was done: both lists are read again, the openings asking Recruitment afresh. */
  const afterAction = useCallback(() => {
    if (projectNow.current !== projectId) return;
    rereadFresh();
    reloadHiringRequests();
    setPastReads((n) => n + 1);
  }, [projectId, rereadFresh, reloadHiringRequests]);

  const rows = list?.links ?? [];
  const counts = list?.page?.counts ?? null;
  const waiting = requestsShown ? hiringRequests.waiting : 0;

  const openPanel = (link: HiringLink) => {
    setPanelAnswer(null);
    setPanelFor(link);
  };
  // The row that was opened, as the list has it now - or, once it has left the rows (a stop,
  // a request that became an opening), the link the panel was last answered with.
  const panelRows = panelFor ? rows.filter((l) => l.id === panelFor.id) : [];
  const panelHeld = panelFor && panelAnswer && panelAnswer.id === panelFor.id ? panelAnswer : panelFor;
  const panelLinks = panelRows.length > 0 ? panelRows : panelHeld ? [panelHeld] : [];
  // The hiring request waiting for the same store. A request with no store is NOT attached to
  // an opening row: every hiring with no store under a budget carries that budget's name, so a
  // name says nothing of which opening it is - it stays in the strip ("N requests waiting").
  const panelRequests: readonly HiringRequest[] = !panelFor || !requestsShown || panelFor.subject.kind !== 'store'
    ? NO_REQUESTS
    : hiringRequests.requests.filter((r) => r.subject.kind === 'store' && !!r.subject.storeId
      && r.subject.storeId.toLowerCase() === String(panelFor.subject.kind === 'store' ? panelFor.subject.storeId ?? '' : '').toLowerCase());

  const requestRows: readonly HiringRequest[] = view === 'requests_past' ? past : view === 'requests_waiting' ? hiringRequests.requests : NO_REQUESTS;
  const requestInWindow = requestOpen
    ? hiringRequests.requests.find((r) => r.id === requestOpen.id) ?? past.find((r) => r.id === requestOpen.id) ?? requestOpen
    : null;

  // ── the Notice period tab's rows ──
  const rowCanRaise = canRaise && raise.canRaise;
  /** The waiting hiring request that replaces a row's person, when this person is shown requests. */
  const requestOfRow = (row: HiringNoticeRow): HiringRequest | null => {
    if (!requestsShown) return null;
    const code = codeOf(row.person.empCode);
    return hiringRequests.requests.find((r) => r.standing === 'waiting' && codeOf(r.replacesEmpCode) === code) ?? null;
  };
  /** The store's own hiring request waits: Ask is disabled for it, as on the Stores page (one request waits per store). */
  const storeRequestWaits = (row: HiringNoticeRow): boolean => hiringRequests.byStore.has(storeHiringKey(row.store.storeId));
  /**
   * A row's store's followed openings, read before a window for that store opens (the row's
   * control spins meanwhile). Not read: `unread` when given, else the reason above the table.
   */
  const withStoreLinks = (row: HiringNoticeRow, open: (links: HiringLink[]) => void, unread?: () => void) => {
    if (!projectId) return;
    const req = ++storeReq.current;
    setStoreOpening(noticeRowKey(row));
    setNoticeError(null);
    Promise.resolve()
      .then(() => hiringLinksAPI.list(projectId, { storeId: row.store.storeId }))
      .then((res) => {
        if (req !== storeReq.current || projectNow.current !== projectId) return;
        const links = res?.data?.data?.links;
        if (!Array.isArray(links)) throw new Error('unreadable store hiring');
        open(links);
      })
      .catch((err: unknown) => {
        if (req !== storeReq.current || projectNow.current !== projectId) return;
        if (isHiringOff(err)) forgetHiringAccess(projectId);
        else if (unread) unread();
        else setNoticeError(hiringErrorText(err, NOTICE_WORDS.storeFailed));
      })
      .finally(() => {
        if (req === storeReq.current) setStoreOpening(null);
      });
  };
  /**
   * "Raise again" (or "Ask for hiring") on an opening deleted in Recruitment, from the opening's
   * window: the Raise window for its store, as a store's row opens it, handed the deleted link.
   * This tab knows a store only by its name and code, so the store is read first - from the
   * Raise window's own store search, by its code - with its followed openings (a raise already
   * in flight there is shown in place of a form; not read, none). The opening's window stays
   * open meanwhile, and says why when the store could not be read. A hiring with no store opens
   * the Raise window with the store optional, as this tab's own button does.
   */
  const openDeletedRaise = (deleted: HiringLink, mode: 'raise' | 'ask'): ReplacingOpen => {
    if (!projectId) return undefined;
    const opened = (subject: RaiseHiringSubject | null, links: readonly HiringLink[]) => {
      setPanelFor(null);
      setPanelAnswer(null);
      setStorePanel(null);
      setDeletedRaise({ mode, subject, links, deleted });
    };
    const store = deleted.subject.kind === 'store' ? deleted.subject : null;
    if (!store?.storeId) {
      opened(null, NO_ROW_LINKS);
      return undefined;
    }
    const storeId = store.storeId;
    const text = String(store.storeCode || store.storeName || '').trim() || undefined;
    const req = ++deletedReq.current;
    return Promise.all([
      mode === 'ask' ? hiringLinksAPI.requestStores(projectId, text) : hiringLinksAPI.raiseStores(projectId, text),
      hiringLinksAPI.list(projectId, { storeId })
        .then((res) => res?.data?.data?.links)
        .catch((err: unknown) => {
          if (isHiringOff(err)) throw err;
          return undefined;
        }),
    ]).then(([res, links]) => {
      if (req !== deletedReq.current || projectNow.current !== projectId) return null;
      const found = res?.data?.data?.stores?.find((s) => s.storeId.toLowerCase() === storeId.toLowerCase());
      if (!found) return DELETED_WORDS.storeNotFound;
      opened(raiseSubjectOfOption(found), Array.isArray(links) ? links : NO_ROW_LINKS);
      return null;
    });
  };
  /** The store's hiring window, from a row whose person hiring already replaces: its followed openings are read first. */
  const openStoreHiring = (row: HiringNoticeRow) => withStoreLinks(row, (links) => setStorePanel({ row, links }));
  /**
   * Raise (or Ask) from a row: the store's openings are read first, so a raise already in
   * flight there is shown in place of the form, as from the Stores page. Not read: the window
   * opens without them - the server's review still refuses a second raise for the store.
   */
  const openRowRaise = (row: HiringNoticeRow, mode: 'raise' | 'ask') =>
    withStoreLinks(row, (links) => setRowRaise({ row, mode, links }), () => setRowRaise({ row, mode, links: NO_ROW_LINKS }));
  const storePanelRequests: readonly HiringRequest[] = !storePanel || !requestsShown
    ? NO_REQUESTS
    : hiringRequests.requests.filter((r) => r.subject.kind === 'store' && !!r.subject.storeId
      && storeHiringKey(r.subject.storeId) === storeHiringKey(storePanel.row.store.storeId));

  // ── the Download ──
  // Of the openings, never of the Notice period tab (people) or the requests.
  const downloadShown = view === 'openings' && !noticeOpen;
  // The tab and search a download is of: its line under the toolbar is shown only while they are.
  const downloadOn = `${projectId ?? ''}|${status}|${search}`;
  // Nothing to download while the tab has nothing - or while its count is not in yet.
  const downloadable = !!list && !loading && total > 0;
  const download = () => {
    if (!projectId || downloading || !downloadable) return;
    const req = ++downloadReq.current;
    const on = downloadOn;
    const forProject = projectId;
    setDownloading(true);
    setDownloadNote(null);
    Promise.resolve()
      .then(() => hiringOpeningAPI.exportHiring(forProject, { status: status || null, q: search || null }))
      .then(async (res) => {
        if (req !== downloadReq.current) return;
        const answer = res?.data?.data;
        if (!answer || !Array.isArray(answer.openings)) throw new Error('unreadable hiring download');
        const now = new Date();
        await downloadTableXlsx(hiringExportFileName(answer.status ?? null, now), hiringExportSheets(answer, now));
        const saved = hiringExportSavedLine(answer);
        if (saved && req === downloadReq.current) setDownloadNote({ on, kind: 'saved', text: saved });
      })
      .catch((err: unknown) => {
        if (req !== downloadReq.current) return;
        // Switched off since the tab opened: the shell takes the tab away.
        if (isHiringOff(err)) forgetHiringAccess(forProject);
        else setDownloadNote({ on, kind: 'error', text: hiringExportErrorText(err) });
      })
      .finally(() => {
        if (req === downloadReq.current) setDownloading(false);
      });
  };
  const downloadLine = downloadShown && downloadNote && downloadNote.on === downloadOn ? downloadNote : null;

  const actions: ReactNode = (
    <>
      {/* The Download: the outlined button, first of the row, for everyone who may see hiring. A
          header button, not a row's: the table Actions rule is not about it. */}
      {downloadShown && (
        <button
          type="button"
          className={`${HIRING_BUTTONS.secondary} gap-2 disabled:cursor-not-allowed disabled:opacity-50`}
          title={DOWNLOAD_WORDS.hover}
          disabled={downloading || !downloadable}
          aria-busy={downloading}
          onClick={download}
        >
          <Download aria-hidden="true" className="h-4 w-4" />
          {downloading ? DOWNLOAD_WORDS.preparing : DOWNLOAD_WORDS.button}
        </button>
      )}
      {/* 1Office's Manage Budgets page, for a person the server offers it to: the outlined button,
          after the Download, saying on hover that openings are raised there too. Draws nothing for
          anybody else. */}
      <ManageBudgetsLink projectId={projectId} active={canRaise} look="secondary" label={DASHBOARD_WORDS.budgetsButton} hover={DASHBOARD_WORDS.budgetsHover} />
      {/* No "Link an opening" here (the product owner, 16 Oct 2026): linking needs the store, so
          it is offered where the store is known - its row and window on Stores. "Link it instead"
          from a raise's review still opens the Link window below. */}
      {/* Only for "Raise & cancel hiring", and when the list says Raise is on and this person may raise here. */}
      {canRaise && raise.canRaise && (
        <button type="button" className={PRIMARY} onClick={() => setRaising(true)}>{DASHBOARD_WORDS.raise}</button>
      )}
      {/* In its place, for a person who may ask and may not raise: saved as a hiring request, nothing is sent. */}
      {canAsk && (
        <button type="button" className={PRIMARY} onClick={() => setAsking(true)}>{DASHBOARD_WORDS.ask}</button>
      )}
    </>
  );

  const now = new Date();
  const readLine = list?.recruitment?.read === 'unavailable' ? DASHBOARD_WORDS.readNone
    : list?.recruitment?.read === 'partial' ? DASHBOARD_WORDS.readPartial : null;

  return (
    <div className="space-y-4">
      {actionSlot ? createPortal(actions, actionSlot) : (
        <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>
      )}

      {view === 'openings' && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">{noticeOpen ? NOTICE_WORDS.searchLabel : DASHBOARD_WORDS.searchLabel}</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={typed}
                maxLength={SEARCH_MAX}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={noticeOpen ? NOTICE_WORDS.searchPlaceholder : DASHBOARD_WORDS.searchPlaceholder}
                autoComplete="off"
                className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </label>
            {/* The house tab strip (StoreMasterPage): an icon, the status and its count in brackets. */}
            <div role="group" aria-label={DASHBOARD_WORDS.filterLabel} className={HOUSE_TABS.tray}>
              {(['', ...DASH_STATUSES] as const).map((value) => {
                const on = !noticeOpen && status === value;
                const count = counts ? (value ? counts[value] : counts.all) : null;
                const Icon = STATUS_ICON[value];
                return (
                  <button
                    key={value || 'all'}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      setShowNotice(false);
                      if (notRaisedOnly) closeList();
                      setStatus(value);
                      setPage(1);
                    }}
                    className={houseTabClass(on)}
                  >
                    <Icon aria-hidden="true" className={HOUSE_TABS.icon} />
                    {withCount(value ? DASH_STATUS_WORDS[value] : DASHBOARD_WORDS.all, count)}
                  </button>
                );
              })}
              {/* The people serving notice at this person's stores: its count follows the search, as the others' do. */}
              <button
                type="button"
                aria-pressed={noticeOpen}
                onClick={() => {
                  // The tab itself is everyone serving notice: picking it lets go of the narrowing.
                  setShowNotice(true);
                  if (notRaisedOnly) closeList();
                }}
                className={houseTabClass(noticeOpen)}
                title={NOTICE_WORDS.tabHover(notice)}
              >
                <CalendarClock aria-hidden="true" className={HOUSE_TABS.icon} />
                {NOTICE_WORDS.tabLabel(notice)}
              </button>
            </div>
          </div>

          {/* The Download's one line: a refusal (the table stays), or what the saved file could not read. */}
          {downloadLine?.kind === 'error' && <p role="alert" className="text-sm text-rose-600">{downloadLine.text}</p>}
          {downloadLine?.kind === 'saved' && <p role="status" className="text-sm text-amber-800">{downloadLine.text}</p>}

          {waiting > 0 && (
            <div role="status" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-amber-600" />
              <span>
                <b className="font-semibold">{waiting}</b>
                {` ${waiting === 1 ? 'request' : 'requests'} waiting · `}
                <button
                  type="button"
                  className="font-semibold text-primary-700 underline underline-offset-2 hover:text-primary-800"
                  onClick={() => setView('requests_waiting')}
                >
                  {DASHBOARD_WORDS.review}
                </button>
              </span>
            </div>
          )}
          {noticeOpen && notRaisedOnly && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-7 items-center gap-1 rounded-full border border-primary-200 bg-primary-50 pl-3 pr-1 text-xs text-primary-800">
                <span className="text-primary-600">{NOTICE_WORDS.columns.hiring}:</span>
                <span className="font-semibold">{NOTICE_WORDS.notRaised}</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotice(true);
                    closeList();
                  }}
                  aria-label={`Remove filter ${NOTICE_WORDS.columns.hiring}: ${NOTICE_WORDS.notRaised}`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            </div>
          )}
          {noticeOpen && (
            <HiringNoticeTable
              list={notice}
              loading={noticeLoading}
              error={noticeError}
              searching={!!search || notRaisedOnly}
              now={now}
              can={{ raise: rowCanRaise, ask: canAsk, link: canManage }}
              askWaiting={storeRequestWaits}
              requestOf={requestOfRow}
              opening={storeOpening}
              onRaise={(row) => openRowRaise(row, 'raise')}
              onAsk={(row) => openRowRaise(row, 'ask')}
              onLink={(row) => setRowLink({ row, pick: null })}
              onOpenStore={openStoreHiring}
              onOpenRequest={setRequestOpen}
              onPage={setNoticePage}
              onTryAgain={() => setNoticeReads((n) => n + 1)}
            />
          )}

          {!noticeOpen && readLine && <p role="status" className="text-xs text-amber-800">{readLine}</p>}
          {!noticeOpen && error && (
            <div className="flex flex-wrap items-center gap-3">
              <FormErrorBanner message={error} />
              <button type="button" className="text-sm font-medium text-primary-600 hover:underline" onClick={rereadFresh}>
                {DASHBOARD_WORDS.tryAgain}
              </button>
            </div>
          )}

          {!noticeOpen && (
            <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
              <div className="overflow-x-auto">
                <table className={TABLE}>
                  <colgroup>
                    {COLUMN_WIDTHS.map((width, i) => <col key={i} className={width || undefined} />)}
                  </colgroup>
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className={TH}>{DASHBOARD_WORDS.columns.store}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.opening}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.address}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.city}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.state}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.status}</th>
                      <th className={`${TH} text-right`}>{DASHBOARD_WORDS.columns.open}</th>
                      <th className={`${TH} text-right`}>{DASHBOARD_WORDS.columns.candidates}</th>
                      <th className={TH}>{DASHBOARD_WORDS.columns.updated}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading && rows.length === 0 ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          <td className={TD}><Skeleton className="h-4 w-3/4" /></td>
                          <td className={TD}><Skeleton className="h-4 w-3/4" /></td>
                          <td className={TD}><Skeleton className="h-4 w-3/4" /></td>
                          <td className={TD}><Skeleton className="h-4 w-2/3" /></td>
                          <td className={TD}><Skeleton className="h-4 w-2/3" /></td>
                          <td className={TD}><Skeleton className="h-4 w-24" /></td>
                          <td className={TD}><Skeleton className="ml-auto h-4 w-6" /></td>
                          <td className={TD}><Skeleton className="ml-auto h-4 w-6" /></td>
                          <td className={TD}><Skeleton className="h-4 w-20" /></td>
                        </tr>
                      ))
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                          {error ? '' : search ? DASHBOARD_WORDS.noneFound : status ? DASHBOARD_WORDS.noneOfStatus(status) : DASHBOARD_WORDS.none}
                        </td>
                      </tr>
                    ) : (
                      rows.map((link) => {
                        const store = storeWords(link);
                        const place = dashPlaceOf(link);
                        return (
                          <tr key={link.id} className="cursor-pointer hover:bg-gray-50" onClick={() => openPanel(link)}>
                            {/* A long store, opening, address, city or state fills its column and ends in "…", whole on hover: the table fits its box at 1360px. */}
                            <td className={TD}>
                              {store
                                ? <span title={store} className="block truncate">{store}</span>
                                : <span className="text-gray-400">{DASHBOARD_WORDS.noStore}</span>}
                            </td>
                            <td className={TD}>
                              <button
                                type="button"
                                aria-label={DASHBOARD_WORDS.openRow(link)}
                                title={[designationOf(link), link.openingId].filter((part) => part != null && part !== '').join(' · ')}
                                className="block w-full truncate text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPanel(link);
                                }}
                              >
                                <span className="font-medium text-gray-900">{designationOf(link)}</span>
                                {link.openingId != null && <span className="text-gray-500">{` · ${link.openingId}`}</span>}
                              </button>
                            </td>
                            {/* Where Recruitment has the opening now (else as it was linked): where candidates are hired. A row with no store too. */}
                            <td className={TD}><PlaceText text={place.address} /></td>
                            <td className={TD}><PlaceText text={place.city} /></td>
                            <td className={TD}><PlaceText text={place.state} /></td>
                            <td className={CELL}><StatusWord link={link} /></td>
                            {/* Deleted in Recruitment: the last numbers read are not what stands now, so neither is shown. */}
                            <td className={`${TD} text-right tabular-nums`}>{numberOrDash(deletedInRecruitment(link) ? null : link.numbers?.open)}</td>
                            <td className={`${TD} text-right tabular-nums`}>{numberOrDash(deletedInRecruitment(link) ? null : candidatesTotal(link.stages))}</td>
                            <td className={`${TD} text-gray-500`}>{updatedWords(link.updatedAt, now)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {list && total > 0 && (
                <HiringPager page={page} lastPage={lastPage} perPage={perPage} total={total} onPage={setPage} />
              )}
            </div>
          )}
        </>
      )}

      {view !== 'openings' && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline"
              onClick={() => setView('openings')}
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4" />
              {DASHBOARD_REQUEST_WORDS.back}
            </button>
            <div role="group" aria-label={DASHBOARD_REQUEST_WORDS.heading} className={HOUSE_TABS.tray}>
              {(['requests_waiting', 'requests_past'] as const).map((value) => {
                const Icon = value === 'requests_waiting' ? Clock : History;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={view === value}
                    onClick={() => setView(value)}
                    className={houseTabClass(view === value)}
                  >
                    <Icon aria-hidden="true" className={HOUSE_TABS.icon} />
                    {value === 'requests_waiting' ? DASHBOARD_REQUEST_WORDS.waitingTab(waiting) : DASHBOARD_REQUEST_WORDS.pastTab}
                  </button>
                );
              })}
            </div>
          </div>
          {view === 'requests_past' && pastError && <FormErrorBanner message={pastError} />}
          <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <th className="whitespace-nowrap px-6 py-3">{DASHBOARD_REQUEST_WORDS.columns.for}</th>
                    <th className="whitespace-nowrap px-6 py-3">{DASHBOARD_REQUEST_WORDS.columns.budget}</th>
                    <th className="whitespace-nowrap px-6 py-3">{DASHBOARD_REQUEST_WORDS.columns.stands}</th>
                    <th className="whitespace-nowrap px-6 py-3">{DASHBOARD_REQUEST_WORDS.columns.askedBy}</th>
                    <th className="whitespace-nowrap px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {pastLoading && requestRows.length === 0 ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-6 w-36 rounded-full" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                        <td className="px-6 py-4"><Skeleton className="mx-auto h-5 w-5" /></td>
                      </tr>
                    ))
                  ) : requestRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                        {view === 'requests_past' ? DASHBOARD_REQUEST_WORDS.nonePast : DASHBOARD_REQUEST_WORDS.noneWaiting}
                      </td>
                    </tr>
                  ) : (
                    requestRows.map((request) => {
                      const name = requestSubjectName(request);
                      return (
                        <tr key={request.id} className={request.standing === 'waiting' ? 'hover:bg-gray-50' : 'bg-gray-50/60'}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="font-medium text-gray-900">{name}</div>
                            <div className="text-xs text-gray-500">{DASHBOARD_REQUEST_WORDS.subjectLine(request)}</div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="text-gray-900">{request.budget?.name}</div>
                            <div className="text-xs text-gray-500">{requestReasonLine(request)}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <HiringRequestPill request={request} />
                              <HiringRequestWaitingTag request={request} />
                            </div>
                            <div className="mt-1 max-w-md text-xs text-gray-600">{requestLine(request)}</div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-gray-600">{requestAskedByShort(request)}</td>
                          <td className="whitespace-nowrap px-6 py-4 text-center">
                            <TableActionGroup>
                              <TableActionButton action="review" label={`Open hiring request for ${name}`}
                                onClick={() => setRequestOpen(request)} />
                            </TableActionGroup>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {view === 'requests_past' && pastCursor && requestRows.length > 0 && (
              <div className="border-t border-gray-100 px-6 py-3 text-center">
                <button
                  type="button"
                  onClick={() => loadPast(pastCursor)}
                  disabled={pastLoading}
                  className="text-sm font-medium text-primary-600 hover:underline disabled:opacity-60"
                >
                  {pastLoading ? DASHBOARD_REQUEST_WORDS.loading : DASHBOARD_REQUEST_WORDS.more}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {panelFor && projectId && windowsOpen && (
        <HiringPanel
          projectId={projectId}
          subject={panelSubjectOf(panelFor)}
          links={panelLinks}
          canManage={canManage}
          // Offered only to a holder of "Raise & cancel hiring" whom the list says may cancel here.
          canCancel={canRaise && cancel.canCancel}
          onClose={() => {
            setPanelFor(null);
            setPanelAnswer(null);
          }}
          // Stopped, cancelled, a request moved on: the panel keeps the link it was answered
          // with, and both lists are read again, asking Recruitment afresh.
          onChanged={(answered) => {
            if (answered && projectNow.current === projectId && panelOpenOn.current === answered.id) setPanelAnswer(answered);
            afterAction();
          }}
          onHiringGone={() => forgetHiringAccess(projectId)}
          // "Raise again", for a hiring with no store that Recruitment deleted or closed: this
          // tab's Raise window. A store's is raised again from its row on the Stores page.
          onRaiseAgain={canRaise && raise.canRaise && panelFor.subject.kind === 'role' ? () => {
            setPanelFor(null);
            setPanelAnswer(null);
            setRaising(true);
          } : undefined}
          // An opening deleted in Recruitment: the Raise window for its store (or Ask for hiring), handed the deleted link.
          onRaiseReplacing={rowCanRaise ? (deleted) => openDeletedRaise(deleted, 'raise') : undefined}
          onAskReplacing={canAsk ? (deleted) => openDeletedRaise(deleted, 'ask') : undefined}
          requests={panelRequests}
          onOpenRequest={(request) => {
            setRequestOpen(request);
            setPanelFor(null);
            setPanelAnswer(null);
          }}
        />
      )}

      {requestInWindow && canSeeRequests && projectId && windowsOpen && (
        <HiringRequestWindow
          projectId={projectId}
          request={requestInWindow}
          can={hiringRequests.can}
          onClose={() => setRequestOpen(null)}
          onRequest={(request) => {
            applyRequest(request);
            rereadFresh();
          }}
          // A confirm made a link: the list is read again and shows it.
          onLink={afterAction}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
          onRaiseGone={() => {
            setRequestOpen(null);
            setRaise(NO_RAISE);
            forgetHiringRequests(projectId);
          }}
        />
      )}

      {/* Ask for hiring: the Raise window in ask mode, the store optional. It is saved as a
          hiring request; nothing is sent to Recruitment from it. */}
      {asking && canAsk && projectId && windowsOpen && (
        <RaiseHiringModal
          mode="ask"
          entry="dashboard"
          projectId={projectId}
          onClose={() => setAsking(false)}
          onChanged={afterAction}
          onAsked={(request) => {
            applyRequest(request);
            rereadFresh();
          }}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
          onRaiseGone={() => {
            setAsking(false);
            setRaise(NO_RAISE);
            forgetHiringRequests(projectId);
          }}
        />
      )}

      {raising && canRaise && projectId && windowsOpen && (
        <RaiseHiringModal
          entry="dashboard"
          projectId={projectId}
          onClose={() => setRaising(false)}
          // The window stays open, showing the result; the list is read again behind it.
          onChanged={afterAction}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
          onRaiseGone={() => {
            setRaising(false);
            setRaise(NO_RAISE);
            forgetHiringRequests(projectId);
          }}
          // What is in the way under a budget: its row, when it is on this page.
          openRequest={(linkId) => {
            const found = rows.find((l) => l.id === linkId);
            if (!found) return null;
            return () => {
              setRaising(false);
              openPanel(found);
            };
          }}
          // Recruitment already has an unlinked opening for it: Link an opening opens on it.
          onLinkInstead={(pick) => {
            setLinkingPick(linkPickOf(pick));
            setRaising(false);
            setLinking(true);
          }}
        />
      )}

      {linking && canManage && projectId && windowsOpen && (
        <LinkHiringModal
          projectId={projectId}
          subject={{ kind: 'role' }}
          initial={linkingPick ?? undefined}
          onClose={() => {
            setLinking(false);
            setLinkingPick(null);
          }}
          onLinked={() => {
            setLinking(false);
            setLinkingPick(null);
            afterAction();
          }}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
        />
      )}

      {/* Raise hiring from a Notice period row: the Raise window as a store's row opens it, for
          that store, "Replacing someone", that person chosen. Ask for hiring is the same window
          in ask mode, for a person who may ask and may not raise: nothing is sent from it. */}
      {rowRaise && projectId && windowsOpen && (rowRaise.mode === 'raise' ? rowCanRaise : canAsk) && (
        <RaiseHiringModal
          mode={rowRaise.mode}
          entry="store"
          projectId={projectId}
          subject={raiseSubjectOfNotice(rowRaise.row)}
          replacing={rowRaise.row.person.empCode}
          // A raise already in flight for the store is shown in place of a form. (No "Edit store"
          // here: the dashboard has no store form, so an incomplete address is fixed on the Stores page.)
          links={rowRaise.links}
          onClose={() => setRowRaise(null)}
          // The window stays open, showing the result; both lists and the tab are read again behind it.
          onChanged={afterAction}
          onAsked={(request) => {
            applyRequest(request);
            afterAction();
          }}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
          onRaiseGone={() => {
            setRowRaise(null);
            setRaise(NO_RAISE);
            forgetHiringRequests(projectId);
          }}
          // What is in the way under a budget: its row, when it is on the openings' page.
          openRequest={(linkId) => {
            const found = rows.find((l) => l.id === linkId);
            if (!found) return null;
            return () => {
              setRowRaise(null);
              openPanel(found);
            };
          }}
          // Recruitment already has an unlinked opening for the store: Link an opening opens on it, for the same store.
          onLinkInstead={rowRaise.mode === 'raise' ? (pick) => {
            const row = rowRaise.row;
            setRowRaise(null);
            setRowLink({ row, pick: { budgetId: pick.budgetId, openingId: pick.openingId } });
          } : undefined}
        />
      )}

      {/* "Raise again" (or "Ask for hiring") on an opening deleted in Recruitment: the Raise window
          for its store, as a store's row opens it, or with the store optional for a hiring with no
          store. A raise sent from it that makes an opening stops following the deleted one. */}
      {deletedRaise && projectId && windowsOpen && (deletedRaise.mode === 'raise' ? rowCanRaise : canAsk) && (
        <RaiseHiringModal
          mode={deletedRaise.mode}
          entry={deletedRaise.subject ? 'store' : 'dashboard'}
          projectId={projectId}
          subject={deletedRaise.subject ?? undefined}
          links={deletedRaise.links}
          replacingDeleted={deletedRaise.deleted}
          onClose={() => setDeletedRaise(null)}
          // The window stays open, showing the result; both lists and the tab are read again behind it.
          onChanged={afterAction}
          onAsked={(request) => {
            applyRequest(request);
            afterAction();
          }}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
          onRaiseGone={() => {
            setDeletedRaise(null);
            setRaise(NO_RAISE);
            forgetHiringRequests(projectId);
          }}
          // What is in the way under a budget: its row, when it is on the openings' page.
          openRequest={(linkId) => {
            const found = rows.find((l) => l.id === linkId);
            if (!found) return null;
            return () => {
              setDeletedRaise(null);
              openPanel(found);
            };
          }}
          // Recruitment already has an unlinked opening for it: Link an opening opens on it, with the store it names.
          onLinkInstead={deletedRaise.mode === 'raise' ? (pick) => {
            setLinkingPick(linkPickOf(pick));
            setDeletedRaise(null);
            setLinking(true);
          } : undefined}
        />
      )}

      {/* Link an opening from a Notice period row: the Link window with the row's store fixed. */}
      {rowLink && canManage && projectId && windowsOpen && (
        <LinkHiringModal
          projectId={projectId}
          subject={{
            kind: 'store',
            storeId: rowLink.row.store.storeId,
            storeName: rowLink.row.store.storeName || rowLink.row.store.storeCode || 'this store',
            storeCode: rowLink.row.store.storeCode ?? '',
          }}
          initial={rowLink.pick ?? undefined}
          onClose={() => setRowLink(null)}
          onLinked={() => {
            setRowLink(null);
            afterAction();
          }}
          onOutOfDate={afterAction}
          onHiringGone={() => forgetHiringAccess(projectId)}
        />
      )}

      {/* The store's hiring window, from a Notice period row whose person hiring already replaces. */}
      {storePanel && projectId && windowsOpen && (
        <HiringPanel
          projectId={projectId}
          subject={{
            kind: 'store',
            storeName: storePanel.row.store.storeName || storePanel.row.store.storeCode || 'this store',
            storeCode: storePanel.row.store.storeCode ?? '',
            storeId: storePanel.row.store.storeId,
          }}
          links={storePanel.links}
          canManage={canManage}
          canCancel={canRaise && cancel.canCancel}
          onClose={() => setStorePanel(null)}
          // A link stopped, cancelled or moved on: the panel shows it as answered, and everything is read again.
          onChanged={(answered) => {
            if (answered) setStorePanel((open) => (open ? { ...open, links: open.links.map((l) => (l.id === answered.id ? answered : l)) } : open));
            afterAction();
          }}
          onHiringGone={() => forgetHiringAccess(projectId)}
          // An opening deleted in Recruitment: the Raise window for this row's store (or Ask for hiring), handed the deleted link.
          onRaiseReplacing={rowCanRaise ? (deleted) => {
            setDeletedRaise({ mode: 'raise', subject: raiseSubjectOfNotice(storePanel.row), links: storePanel.links, deleted });
            setStorePanel(null);
          } : undefined}
          onAskReplacing={canAsk ? (deleted) => {
            setDeletedRaise({ mode: 'ask', subject: raiseSubjectOfNotice(storePanel.row), links: storePanel.links, deleted });
            setStorePanel(null);
          } : undefined}
          requests={storePanelRequests}
          onOpenRequest={(request) => {
            setRequestOpen(request);
            setStorePanel(null);
          }}
        />
      )}
    </div>
  );
}
